import { useState, useEffect } from "react";
import styles from "./styles/footer";
import { Hidden } from "@material-ui/core";
import { withTranslation } from "../../i18n";
import { showSuccessAlert, showErrorAlert } from "../../helpers/general";
import { subscribeNews } from "../../helpers/api";
import link from "../../static/text/link";
import { getCFCountry } from "../../helpers/setCookie";

const logoMfs = require("../../static/new-landing-page/logo/mfs_logo_for_black.webp");

const Footer = ({ t, i18n, hideLanguageButton: hideLanguageButtonProp = false }) => {
    const classes = styles();
    const [hideLanguageButtonState, setHideLanguageButtonState] = useState(false);
    useEffect(() => {
        const cfCountry = getCFCountry();
        const isOutsideIndonesia = cfCountry && String(cfCountry).toUpperCase() !== "ID";
        setHideLanguageButtonState(!!isOutsideIndonesia);
    }, []);
    const hideLanguageButton = hideLanguageButtonProp || hideLanguageButtonState;
    const [email, setEmail] = useState("");
    const [isLanguageOpen, setIsLanguageOpen] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
    const [isLoading, setIsLoading] = useState(false);

    // Email validation function
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubscribe = async (e) => {
        e.preventDefault();

        if (!isValidEmail(email)) {
            return;
        }

        setIsLoading(true);
        try {
            const res = await subscribeNews({
                email: email,
                language_code: selectedLanguage
            });
            setEmail("");
            showSuccessAlert(null, t("success_enter_email"), undefined, "medium")
        } catch (err) {

            // Map error message from API response
            const getErrorMessage = (errorObj) => {
                // Handle case where error.response.data or error.data is already the response body
                let errorData = null;

                if (errorObj && errorObj.data) {
                    errorData = errorObj.data;
                } else if (errorObj && errorObj.response && errorObj.response.data) {
                    errorData = errorObj.response.data;
                } else if (errorObj) {
                    errorData = errorObj;
                }

                if (!errorData) {
                    return t("error_subscribe_newsletter") || "An error occurred while subscribing to newsletter.";
                }

                // Extract error message from data object (e.g., data.email[0])
                if (errorData.data) {
                    const fieldErrors = Object.values(errorData.data);
                    if (fieldErrors.length > 0 && Array.isArray(fieldErrors[0]) && fieldErrors[0].length > 0) {
                        return fieldErrors[0][0];
                    }
                }

                // Fallback to meta message
                if (errorData.meta && errorData.meta.message) {
                    return errorData.meta.message;
                }

                return t("error_subscribe_newsletter") || "An error occurred while subscribing to newsletter.";
            };

            const errorMessage = getErrorMessage(err);
            showErrorAlert(null, errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLanguageChange = (language) => {
        setSelectedLanguage(language);
        setIsLanguageOpen(false);
        window.location.href = languages.find(lang => lang.code === language).redirect;
    };


    const languages = [
        { code: "en", name: t("english"), flag: "/static/images/mfs/flag_us.png", redirect: "/lang/en" },
        { code: "id", name: t("indonesian"), flag: "/static/images/mfs/flag_id.png", redirect: "/lang/id" }
    ];

    // languages[] only carries en/id, but i18n.js ships de/ar/zh/pt too:
    // without the English fallback this is undefined and currentLanguage.flag
    // below kills the whole page during SSR.
    const currentLanguage = languages.find(lang => lang.code === selectedLanguage) || languages[0];


    function renderNewsletter() {
        const isEmailValid = isValidEmail(email);
        const isButtonDisabled = !isEmailValid || isLoading;

        return (
            <div className={classes.newsletterSection}>
                <span className={classes.newsletterTitle}>{t("subscribe_to_newsletter")}</span>
                <form onSubmit={handleSubscribe} className={classes.newsletterForm}>
                    <input
                        type="email"
                        placeholder={t("enter_email")}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={classes.emailInput}
                        required
                    />
                    <button
                        type="submit"
                        className={`${classes.subscribeButton} ${isButtonDisabled ? classes.disabledButton : ''}`}
                        disabled={isButtonDisabled}
                    >
                        {isLoading ? (
                            <div className={classes.loadingContainer}>
                                <div className={classes.spinner}></div>
                            </div>
                        ) : (
                            t("subscribe")
                        )}
                    </button>
                </form>
            </div>
        )
    }

    function renderStore() {
        return (
            <div className={classes.footerColumn}>
                <div className={classes.companyInfo}>
                    <div className={classes.logo}>
                        <img src={logoMfs} alt="logo" loading="lazy" />
                    </div>
                </div>

                <div className={classes.appRatings}>
                    <div className={classes.ratingItem}>
                        <span className={classes.storeName}>APPLE APP STORE</span>
                        <div className={classes.ctnRating}>
                            <div className={classes.stars}>
                                <img src="/static/star_white.png" alt="" loading="lazy" />
                                <img src="/static/star_white.png" alt="" loading="lazy" />
                                <img src="/static/star_white.png" alt="" loading="lazy" />
                                <img src="/static/star_white.png" alt="" loading="lazy" />
                                <img src="/static/star_white.png" alt="" loading="lazy" />
                            </div>
                            <span className={classes.ratingScore}>4.8</span>
                        </div>
                    </div>

                    <div className={classes.ratingItem}>
                        <span className={classes.storeName}>GOOGLE PLAY STORE</span>
                        <div className={classes.ctnRating}>
                            <div className={classes.stars}>
                                <img src="/static/star_white.png" alt="" loading="lazy" />
                                <img src="/static/star_white.png" alt="" loading="lazy" />
                                <img src="/static/star_white.png" alt="" loading="lazy" />
                                <img src="/static/star_white.png" alt="" loading="lazy" />
                                <img src="/static/star_white.png" alt="" loading="lazy" />
                            </div>
                            <span className={classes.ratingScore}>4.8</span>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function renderLinks() {
        return (
            <>
                <div className={`${classes.footerColumn} ${classes.mobileLinkList}`}>
                    <h4 className={classes.columnTitle}>{t('about').toUpperCase()}</h4>
                    <ul className={classes.linkList}>
                        <li><a href={link.mobile.static("about")} className={classes.footerLink}>{t('about_us')}</a></li>
                        <li><a href={link.mobile.static("contact-us")} className={classes.footerLink}>{t('contact_us')}</a></li>
                    </ul>
                </div>

                {/* Column 3 - FEATURES Links */}
                <div className={`${classes.footerColumn} ${classes.mobileLinkList}`}>
                    <h4 className={classes.columnTitle}>{t('features').toUpperCase()}</h4>
                    <ul className={classes.linkList}>
                        <li><a href={link.mobile.articles} className={classes.footerLink}>{t('articles')}</a></li>
                        <li><a href={link.mobile.foodDatabase} className={classes.footerLink}>{t('mobile-landing:header_food_database')}</a></li>
                        <li><a href={link.mobile.downloadAppRoot} className={classes.footerLink}>{t('community')}</a></li>
                        <li><a href={link.mobile.tools} className={classes.footerLink}>{t('fit_tools')}</a></li>
                    </ul>
                </div>

                {/* Column 4 - LEGAL Links */}
                <div className={`${classes.footerColumn} ${classes.mobileLinkList}`}>
                    <h4 className={classes.columnTitle}>{t('legal').toUpperCase()}</h4>
                    <ul className={classes.linkList}>
                        <li><a href={link.mobile.static("privacy-policy")} className={classes.footerLink}>{t('privacy_policy')}</a></li>
                        <li><a href={link.mobile.static("terms-of-use")} className={classes.footerLink}>{t('terms_of_use')}</a></li>
                        <li><a href={link.mobile.static("cookie-policy")} className={classes.footerLink}>Cookie Policy</a></li>
                        <li><a href={link.mobile.static("refund-policy")} className={classes.footerLink}>{t('refund_policy')}</a></li>
                        <li><a href={link.mobile.static("consumer-health-data-privacy")} className={classes.footerLink}>{t('washington_health_data_policy')}</a></li>
                        <li><a href={link.mobile.static("nevada-health-data-policy")} className={classes.footerLink}>{t('nevada_health_data_policy')}</a></li>
                    </ul>
                </div>
            </>
        )
    }

    function renderBottom() {
        return (
            <div className={classes.bottomSection}>
                <div className={classes.bottomContent}>
                    <div className={classes.copyright}>
                        © {new Date().getFullYear()} Myfitsociety. All rights reserved.
                    </div>
                    {!hideLanguageButton && (
                        <div className={classes.languageSelectorContainer}>

                            {isLanguageOpen && (
                                <div className={classes.languageDropdown}>
                                    {languages.map((language) => (
                                        <div
                                            key={language.code}
                                            className={`${classes.languageOption} ${selectedLanguage === language.code ? classes.selectedLanguage : ''}`}
                                            onClick={() => handleLanguageChange(language.code)}
                                        >
                                            <img src={language.flag} alt={`${language.name} Flag`} className={classes.flagIcon} loading="lazy" />
                                            <span>{language.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div
                                className={classes.languageSelector}
                                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                            >
                                <img src={currentLanguage.flag} alt={`${currentLanguage.name} Flag`} className={classes.flagIcon} loading="lazy" />
                                <span>{currentLanguage.name}</span>
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    className={`${classes.dropdownArrow} ${isLanguageOpen ? classes.rotateArrow : ''}`}
                                >
                                    <path d="M6 9L12 15L18 9" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <footer className={classes.root}>
            <div className={classes.container}>
                <Hidden smDown>
                    {renderNewsletter()}
                </Hidden>
                <Hidden smUp>
                    {renderStore()}
                </Hidden>
                {/* Main Footer Content */}
                <div className={classes.mainFooter}>
                    <div className={classes.footerColumns}>
                        <Hidden xsDown>
                            {renderStore()}
                        </Hidden>
                        <Hidden mdUp>
                            {renderNewsletter()}
                        </Hidden>
                        {renderLinks()}
                    </div>
                </div>

                {/* Bottom Section */}
                {renderBottom()}
            </div>
        </footer>
    );
};

export default withTranslation(["myfitsociety"])(Footer);

